import type { VideoScreenInsightRow, VideoScreenInsightSection } from '../../../services/api.service';

export type DeviationTone = 'positive' | 'negative' | 'neutral';

export type KeyMetric = {
  id: string;
  sectionId: string;
  label: string;
  /** Основное число — всегда сумма/факт (ожид. факт, факт, прогноз). */
  value: string;
  valueCaption: string;
  /** Отклонение к плану — всегда во второй строке карточки. */
  deviation?: string;
  deviationCaption?: string;
  deviationTone?: DeviationTone;
  kind: 'total' | 'highlight';
};

export type ChartBarItem = {
  label: string;
  value: number;
  displayValue: string;
  tone: DeviationTone;
};

const EMPTY_MARKERS = new Set(['', '—', '-', '–', 'null', 'undefined', 'n/a', 'нет']);

const TOTAL_LABEL_RE = /^(всего|итого|total)$/i;

const LABEL_COLUMN_PATTERNS = [
  /^филиал$/i,
  /^направление$/i,
  /^показатель$/i,
  /^статья$/i,
  /^регион/i,
];

const DEVIATION_COLUMN_PATTERNS = [
  /% плана/i,
  /^отклонение$/i,
  /^%$/,
];

export function sectionAnchorId(index: number, title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return `vi-table-${index}-${slug || 'section'}`;
}

export function isEmptyCell(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  return EMPTY_MARKERS.has(s.toLowerCase());
}

export function formatCellValue(value: unknown): string {
  if (isEmptyCell(value)) return '—';
  return String(value).trim();
}

export function isTotalRow(row: VideoScreenInsightRow, columns: string[]): boolean {
  const firstCol = columns[0];
  if (!firstCol) return false;
  const label = formatCellValue(row[firstCol]);
  return TOTAL_LABEL_RE.test(label);
}

export function isLabelColumn(col: string): boolean {
  return LABEL_COLUMN_PATTERNS.some(re => re.test(col.trim()));
}

export function isNumericColumn(col: string, rows: VideoScreenInsightRow[]): boolean {
  if (isLabelColumn(col)) return false;
  if (DEVIATION_COLUMN_PATTERNS.some(re => re.test(col.trim()))) return true;
  const sample = rows
    .filter(r => !isTotalRow(r, Object.keys(r)))
    .slice(0, 8)
    .map(r => formatCellValue(r[col]));
  const numericHits = sample.filter(v => {
    if (isEmptyCell(v)) return false;
    return /^[+\-−]?\d[\d\s.,]*%?$/.test(v.replace(/\s/g, ''));
  });
  return numericHits.length >= Math.max(1, Math.ceil(sample.length * 0.5));
}

export function isDeviationColumn(col: string): boolean {
  return DEVIATION_COLUMN_PATTERNS.some(re => re.test(col.trim()));
}

export function parseNumericValue(raw: string): number | null {
  const s = raw
    .trim()
    .replace(/\s/g, '')
    .replace(/,/g, '.')
    .replace(/−/g, '-')
    .replace(/%/g, '');
  if (!s || s === '—') return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export function deviationTone(value: string, column: string): DeviationTone {
  if (isEmptyCell(value)) return 'neutral';
  const n = parseNumericValue(value);
  if (n === null) {
    if (/^[+]/.test(value.trim())) return 'positive';
    if (/^[-−]/.test(value.trim())) return 'negative';
    return 'neutral';
  }
  if (/% плана/i.test(column) || /^%$/.test(column.trim())) {
    if (n > 100) return 'positive';
    if (n < 100) return 'negative';
    return 'neutral';
  }
  if (/отклонение/i.test(column)) {
    if (n > 0) return 'positive';
    if (n < 0) return 'negative';
    return 'neutral';
  }
  if (n > 0) return 'positive';
  if (n < 0) return 'negative';
  return 'neutral';
}

export function detectSuspiciousCells(
  rows: VideoScreenInsightRow[],
  columns: string[],
): Set<string> {
  const suspicious = new Set<string>();
  const dataRows = rows.filter(r => !isTotalRow(r, columns));

  columns.forEach(col => {
    if (isLabelColumn(col)) return;
    const values = dataRows.map((row, rIdx) => ({
      rIdx,
      val: formatCellValue(row[col]),
    }));
    const filled = values.filter(v => !isEmptyCell(v.val));
    if (filled.length < 2) return;
    const fillRatio = filled.length / values.length;
    if (fillRatio < 0.45) return;

    values.forEach(({ rIdx, val }) => {
      if (isEmptyCell(val)) {
        suspicious.add(`${rIdx}:${col}`);
      }
      const n = parseNumericValue(val);
      if (n === 0 && filled.length >= 3) {
        suspicious.add(`${rIdx}:${col}`);
      }
    });
  });

  return suspicious;
}

export function pickDeviationColumn(columns: string[]): string | null {
  for (const col of columns) {
    if (isDeviationColumn(col)) return col;
  }
  return null;
}

export function buildChartData(
  section: VideoScreenInsightSection,
): { column: string; items: ChartBarItem[] } | null {
  const columns = section.columns || [];
  const rows = section.rows || [];
  const deviationCol = pickDeviationColumn(columns);
  if (!deviationCol) return null;

  const labelCol = columns.find(isLabelColumn) || columns[0];
  if (!labelCol) return null;

  const dataRows = rows.filter(r => !isTotalRow(r, columns));
  if (dataRows.length < 3) return null;

  const items: ChartBarItem[] = dataRows
    .map(row => {
      const label = formatCellValue(row[labelCol]);
      const displayValue = formatCellValue(row[deviationCol]);
      const value = parseNumericValue(displayValue);
      if (value === null || isEmptyCell(label)) return null;
      return {
        label,
        value,
        displayValue,
        tone: deviationTone(displayValue, deviationCol),
      };
    })
    .filter((item): item is ChartBarItem => item !== null);

  if (items.length < 3) return null;
  return { column: deviationCol, items };
}

function pickPrimaryAmountColumn(
  columns: string[],
  labelCol: string,
  deviationCol: string | null,
  rows: VideoScreenInsightRow[],
): string | null {
  const candidates = columns.filter(
    c =>
      c !== labelCol
      && c !== deviationCol
      && !/рост/i.test(c)
      && isNumericColumn(c, rows),
  );
  const preferred = [
    /ожид/i,
    /^факт/i,
    /прогноз/i,
    /план 5/i,
    /выручка/i,
  ];
  for (const pattern of preferred) {
    const col = candidates.find(c => pattern.test(c));
    if (col) return col;
  }
  return candidates[0] || null;
}

function sectionPriority(title: string): number {
  const t = title.toLowerCase();
  if (t.includes('направлен')) return 100;
  if (t.includes('филиал')) return 90;
  if (t.includes('starlink')) return 80;
  if (t.includes('расход')) return 70;
  if (t.includes('b2b')) return 60;
  return 10;
}

function deviationCaptionForColumn(col: string | null): string {
  if (!col) return 'к плану';
  if (/% плана/i.test(col)) return '% плана';
  if (/отклонение/i.test(col)) return 'отклонение';
  if (col.trim() === '%') return '% плана';
  return 'к плану';
}

function valueCaptionForColumn(col: string | null): string {
  if (!col) return 'тыс. тг';
  const c = col.toLowerCase();
  if (c.includes('ожид')) return 'ожид. факт';
  if (c.includes('факт')) return 'факт';
  if (c.includes('прогноз')) return 'прогноз';
  if (c.includes('план')) return 'план';
  return 'тыс. тг';
}

export function extractKeyMetrics(sections: VideoScreenInsightSection[]): KeyMetric[] {
  const totals: { metric: KeyMetric; order: number }[] = [];
  const highlights: KeyMetric[] = [];

  sections.forEach((section, sIdx) => {
    const sectionId = sectionAnchorId(sIdx, section.title);
    const order = sectionPriority(section.title);
    const columns = section.columns || [];
    const rows = section.rows || [];
    const labelCol = columns.find(isLabelColumn) || columns[0];
    const deviationCol = pickDeviationColumn(columns);
    if (!labelCol) return;

    const amountCol = pickPrimaryAmountColumn(columns, labelCol, deviationCol, rows);

    rows.forEach(row => {
      const isTotal = isTotalRow(row, columns);
      const rowLabel = formatCellValue(row[labelCol]);
      const deviation = deviationCol ? formatCellValue(row[deviationCol]) : '—';
      const amount = amountCol ? formatCellValue(row[amountCol]) : '—';

      if (isTotal) {
        if (amount === '—' && deviation === '—') return;
        totals.push({
          metric: {
            id: `${sectionId}-total`,
            sectionId,
            label: metricLabelForSection(section.title, true),
            value: amount !== '—' ? amount : deviation,
            valueCaption: amount !== '—'
              ? valueCaptionForColumn(amountCol)
              : deviationCaptionForColumn(deviationCol),
            deviation: amount !== '—' && deviation !== '—' ? deviation : undefined,
            deviationCaption: deviation !== '—' ? deviationCaptionForColumn(deviationCol) : undefined,
            deviationTone: deviation !== '—' && deviationCol
              ? deviationTone(deviation, deviationCol)
              : undefined,
            kind: 'total',
          },
          order,
        });
        return;
      }

      if (!deviationCol || deviation === '—') return;
      const n = parseNumericValue(deviation);
      if (n === null || Math.abs(n) < 25) return;

      highlights.push({
        id: `${sectionId}-${rowLabel}`,
        sectionId,
        label: rowLabel,
        value: amount !== '—' ? amount : deviation,
        valueCaption: amount !== '—'
          ? valueCaptionForColumn(amountCol)
          : deviationCaptionForColumn(deviationCol),
        deviation: amount !== '—' ? deviation : undefined,
        deviationCaption: amount !== '—' ? deviationCaptionForColumn(deviationCol) : undefined,
        deviationTone: deviationTone(deviation, deviationCol),
        kind: 'highlight',
      });
    });
  });

  totals.sort((a, b) => b.order - a.order);

  const picked: KeyMetric[] = totals.map(t => t.metric);

  const highlightScores = highlights.map(h => {
    const devN = Math.abs(parseNumericValue(h.deviation || h.value) ?? 0);
    const isPct = /%/.test(h.deviation || h.value);
    return { metric: h, score: devN * (isPct ? 1 : 0.000001) };
  });
  highlightScores.sort((a, b) => b.score - a.score);

  for (const { metric } of highlightScores) {
    if (picked.length >= 6) break;
    if (picked.some(p => p.id === metric.id)) continue;
    if (picked.some(p => p.sectionId === metric.sectionId && p.kind === 'total')) {
      continue;
    }
    picked.push(metric);
  }

  return picked.slice(0, 6);
}

export function sectionNavLabel(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('филиал')) return 'Филиалы';
  if (t.includes('расход')) return 'Расходы';
  if (t.includes('starlink')) return 'Starlink';
  if (t.includes('направлен')) return 'Направления';
  if (t.includes('b2b')) return 'B2B';
  const words = title.split(/[\s/]+/).filter(Boolean);
  return words.slice(0, 2).join(' ');
}

function metricLabelForSection(title: string, isTotal: boolean): string {
  const t = title.toLowerCase();
  if (t.includes('направлен')) return isTotal ? 'Выручка всего (B2O+B2B+B2R)' : 'Направление';
  if (t.includes('starlink')) return isTotal ? 'Starlink (итого 5 мес.)' : 'Starlink';
  if (t.includes('расход')) return isTotal ? 'Расходы R2B (прогноз)' : 'Расходы';
  if (t.includes('филиал')) return isTotal ? 'Выручка R2B (всего)' : 'Филиал';
  if (t.includes('b2b')) return isTotal ? 'Выручка B2B (итого)' : 'B2B';
  return shortSectionTitle(title);
}

function shortSectionTitle(title: string): string {
  if (title.length <= 28) return title;
  return title.replace(/^Выручка по /i, '').slice(0, 28);
}

export function isQuoteHighlight(text: string): boolean {
  return /«|»|:\s*«/.test(text) || /^\d+:\d+/.test(text.trim());
}

export function buildContextLine(meetingName?: string, sectionCount?: number): string {
  const name = meetingName?.trim() || 'Совещание';
  const tables = sectionCount ? ` · ${sectionCount} таблиц` : '';
  return `${name} · OCR с кадров Teams (Excel)${tables}`;
}

export function buildSourceMetaLine(section: VideoScreenInsightSection): string {
  const parts: string[] = ['OCR · кадр презентации'];
  if (section.source_file) parts.push(section.source_file);
  if (section.presenter) parts.push(section.presenter);
  if (section.timestamp) parts.push(section.timestamp);
  if (section.unit) parts.push(`ед.: ${section.unit}`);
  return parts.join(' · ');
}
